import React, { useState, useEffect } from 'react';
import './HostedPdfConnectionForm.css';

/**
 * Hosted PDF connection form (PRD-808).
 *
 * Two modes:
 *   - stable_urls: textarea, one URL per line
 *   - index_url:   one URL pointing at a JSON / RSS / HTML index document
 *
 * Presentational only — owns local state, calls onChange with the serialized
 * payload each render. Parent (AddConnectionModal) decides when to POST.
 *
 * `initialValues` (edit mode) seeds every field from the connection's config.
 */
const HostedPdfConnectionForm = ({ onChange, initialValues }) => {
  const initial = initialValues || {};
  const [mode, setMode] = useState(initial.mode || 'stable_urls');
  const [urlsText, setUrlsText] = useState(
    Array.isArray(initial.urls) ? initial.urls.join('\n') : (initial.urls || '')
  );
  const [indexUrl, setIndexUrl] = useState(initial.index_url || '');
  const [indexFormat, setIndexFormat] = useState(initial.index_format || 'json');
  const [indexJsonPath, setIndexJsonPath] = useState(initial.index_jsonpath || '$.documents[*].url');
  const [requestTimeoutS, setRequestTimeoutS] = useState(
    typeof initial.request_timeout_s === 'number' && initial.request_timeout_s > 0 ? initial.request_timeout_s : 30
  );

  // Edit mode: emit the seeded payload up so the parent's submit path sees
  // a valid value even if the user submits without touching anything.
  useEffect(() => {
    if (!initialValues) return;
    const next = { mode: initial.mode || 'stable_urls', request_timeout_s: requestTimeoutS };
    if (next.mode === 'stable_urls') {
      next.urls = Array.isArray(initial.urls) ? initial.urls : [];
    } else {
      next.index_url = initial.index_url || '';
      next.index_format = initial.index_format || 'json';
      if (next.index_format === 'json') {
        next.index_jsonpath = initial.index_jsonpath || '$.documents[*].url';
      }
    }
    onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (overrides = {}) => {
    const m = overrides.mode ?? mode;
    const next = {
      mode: m,
      request_timeout_s: Number(overrides.requestTimeoutS ?? requestTimeoutS) || 30,
    };
    if (m === 'stable_urls') {
      next.urls = parseUrls(overrides.urlsText ?? urlsText);
    } else {
      next.index_url = (overrides.indexUrl ?? indexUrl).trim();
      next.index_format = overrides.indexFormat ?? indexFormat;
      if (next.index_format === 'json') {
        next.index_jsonpath = (overrides.indexJsonPath ?? indexJsonPath).trim();
      }
    }
    onChange(next);
  };

  return (
    <div className="hosted-pdf-form">
      <div className="connection-modal-field">
        <label>Mode</label>
        <div className="hosted-pdf-mode-toggle">
          <label className="resui-radio">
            <input
              type="radio"
              className="resui-radio-input"
              name="hosted-pdf-mode"
              value="stable_urls"
              checked={mode === 'stable_urls'}
              onChange={() => {
                setMode('stable_urls');
                emit({ mode: 'stable_urls' });
              }}
            />
            <span className="resui-radio-mark" />
            <span className="resui-radio-label">Stable URLs</span>
          </label>
          <label className="resui-radio">
            <input
              type="radio"
              className="resui-radio-input"
              name="hosted-pdf-mode"
              value="index_url"
              checked={mode === 'index_url'}
              onChange={() => {
                setMode('index_url');
                emit({ mode: 'index_url' });
              }}
            />
            <span className="resui-radio-mark" />
            <span className="resui-radio-label">Index URL</span>
          </label>
        </div>
        <p className="connection-modal-helper-text">
          Stable URLs: paste an explicit list of PDF URLs. Index URL: a URL pointing at a JSON / RSS / HTML index document listing PDFs.
        </p>
      </div>

      {mode === 'stable_urls' && (
        <div className="connection-modal-field">
          <label htmlFor="hp-urls">PDF URLs</label>
          <textarea
            id="hp-urls"
            value={urlsText}
            onChange={(e) => {
              setUrlsText(e.target.value);
              emit({ urlsText: e.target.value });
            }}
            placeholder={'https://example.com/doc1.pdf\nhttps://example.com/doc2.pdf'}
            rows={6}
            required
          />
          <p className="connection-modal-helper-text">One URL per line. Must be http(s); only public URLs are supported in v1.</p>
        </div>
      )}

      {mode === 'index_url' && (
        <>
          <div className="connection-modal-field">
            <label htmlFor="hp-index-url">Index URL</label>
            <input className="resui-input"
              id="hp-index-url"
              type="url"
              value={indexUrl}
              onChange={(e) => {
                setIndexUrl(e.target.value);
                emit({ indexUrl: e.target.value });
              }}
              placeholder="https://example.com/manifest.json"
              required
            />
            <p className="connection-modal-helper-text">URL of the index document. Re-fetched each run so updates don't require re-saving.</p>
          </div>

          <div className="connection-modal-field">
            <label htmlFor="hp-index-format">Index Format</label>
            <select
              className="resui-select"
              id="hp-index-format"
              value={indexFormat}
              onChange={(e) => {
                setIndexFormat(e.target.value);
                emit({ indexFormat: e.target.value });
              }}
            >
              <option value="json">JSON</option>
              <option value="rss">RSS / Atom feed</option>
              <option value="html">HTML page (scrape &lt;a href$=".pdf"&gt;)</option>
            </select>
          </div>

          {indexFormat === 'json' && (
            <div className="connection-modal-field">
              <label htmlFor="hp-index-jsonpath">JSONPath</label>
              <input className="resui-input"
                id="hp-index-jsonpath"
                type="text"
                value={indexJsonPath}
                onChange={(e) => {
                  setIndexJsonPath(e.target.value);
                  emit({ indexJsonPath: e.target.value });
                }}
                placeholder="$.documents[*].url"
                required
              />
              <p className="connection-modal-helper-text">JSONPath expression returning a list of PDF URL strings.</p>
            </div>
          )}
        </>
      )}

      <div className="connection-modal-field">
        <label htmlFor="hp-timeout">Request Timeout (seconds)</label>
        <input className="resui-input"
          id="hp-timeout"
          type="number"
          min="1"
          max="300"
          value={requestTimeoutS}
          onChange={(e) => {
            setRequestTimeoutS(e.target.value);
            emit({ requestTimeoutS: e.target.value });
          }}
        />
      </div>
    </div>
  );
};

/**
 * Parse the textarea into a clean list of URLs. Skips blank lines and trims
 * whitespace. Returns null when empty so the backend's Pydantic validator
 * surfaces a clear error.
 */
function parseUrls(raw) {
  if (raw === null || raw === undefined) return null;
  const lines = String(raw)
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  return lines.length > 0 ? lines : null;
}

export default HostedPdfConnectionForm;
